import { useMemo, useState } from 'react';
import { Lock, RotateCcw, Save, Globe } from 'lucide-react';

import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  ErrorAlert,
  Field,
  Input,
  PageHeader,
  Skeleton,
  Textarea,
} from '@/components/ui';
import { useAuth } from '@/lib/auth-context';
import {
  SETTING_GROUP_LABEL,
  SETTING_GROUP_ORDER,
  useSaveSettings,
  useSettings,
  type AdminSetting,
  type SettingGroup,
} from '@/lib/settings-api';
import { cn, errorMessage, fieldErrors, formatDateTime } from '@/lib/utils';

/**
 * System settings (spec §78).
 *
 * The screen behind rule 3: no deposit minimum, MOQ default, GST rate or
 * quotation validity is hardcoded anywhere, which is only true if there is
 * somewhere to edit them. Until this existed they were editable in MySQL.
 *
 * Two things it deliberately does not do. It does not validate business ranges
 * in the browser — the server owns every constraint and returns field errors
 * keyed by setting key, so a rule can tighten server-side without a frontend
 * release. And it does not save on change: these values are read together, and
 * a half-applied group prices orders wrongly, so the whole batch goes at once.
 */

/** A value long enough that a single-line input would hide most of it. */
const TEXTAREA_THRESHOLD = 90;

type Draft = Record<string, unknown>;

export function SettingsPage() {
  const { user } = useAuth();
  const settings = useSettings();
  const save = useSaveSettings();

  const [group, setGroup] = useState<SettingGroup | 'ALL'>('ALL');
  const [draft, setDraft] = useState<Draft>({});

  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const errors = fieldErrors(save.error);

  const groups = useMemo(() => {
    const present = new Set((settings.data ?? []).map((setting) => setting.group));
    return SETTING_GROUP_ORDER.filter((entry) => present.has(entry));
  }, [settings.data]);

  const visible = (settings.data ?? []).filter(
    (setting) => group === 'ALL' || setting.group === group,
  );

  // A key is dirty only while its draft differs from the stored value: typing a
  // change and typing it back should leave nothing to save.
  const dirtyKeys = Object.keys(draft).filter((key) => {
    const setting = settings.data?.find((entry) => entry.key === key);
    return setting ? !isSame(setting.value, draft[key]) : false;
  });

  const handleSave = () => {
    save.mutate(
      dirtyKeys.map((key) => ({ key, value: draft[key] })),
      { onSuccess: () => setDraft({}) },
    );
  };

  return (
    <div className="pb-24">
      <PageHeader
        title="General Settings"
        description="Every business rule the platform enforces. Changed here, enforced everywhere."
      />

      {settings.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-10" />
          <Skeleton className="h-64" />
        </div>
      ) : settings.isError ? (
        <ErrorAlert title="Could not load settings">{errorMessage(settings.error)}</ErrorAlert>
      ) : (
        <>
          <Card className="mb-4 p-4">
            <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Settings group">
              {(['ALL', ...groups] as Array<SettingGroup | 'ALL'>).map((value) => (
                <button
                  key={value}
                  type="button"
                  role="tab"
                  aria-selected={group === value}
                  onClick={() => setGroup(value)}
                  className={cn(
                    'rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
                    group === value
                      ? 'bg-brand-600 text-white'
                      : 'text-ink-muted hover:bg-canvas hover:text-ink',
                  )}
                >
                  {value === 'ALL' ? 'Everything' : SETTING_GROUP_LABEL[value]}
                </button>
              ))}
            </div>
          </Card>

          {visible.length === 0 ? (
            <EmptyState title="Nothing configured in this group" />
          ) : (
            <div className="space-y-4">
              {groups
                .filter((entry) => group === 'ALL' || entry === group)
                .map((entry) => (
                  <SettingsGroupCard
                    key={entry}
                    group={entry}
                    settings={visible.filter((setting) => setting.group === entry)}
                    draft={draft}
                    errors={errors}
                    isSuperAdmin={isSuperAdmin}
                    onChange={(key, value) =>
                      setDraft((current) => ({ ...current, [key]: value }))
                    }
                  />
                ))}
            </div>
          )}
        </>
      )}

      {dirtyKeys.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-line bg-surface/95 px-6 py-3 backdrop-blur">
          <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-ink">
                {dirtyKeys.length} {dirtyKeys.length === 1 ? 'change' : 'changes'} not saved
              </p>
              <p className="truncate text-xs text-ink-subtle">{dirtyKeys.join(', ')}</p>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setDraft({})} disabled={save.isPending}>
                <RotateCcw className="size-4" aria-hidden="true" />
                Discard
              </Button>
              <Button onClick={handleSave} disabled={save.isPending}>
                <Save className="size-4" aria-hidden="true" />
                {save.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </div>

          {save.isError && (
            <div className="mx-auto mt-2 max-w-5xl">
              <ErrorAlert title="Nothing was saved">{errorMessage(save.error)}</ErrorAlert>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ==========================================================================
   One group
   ========================================================================== */

function SettingsGroupCard({
  group,
  settings,
  draft,
  errors,
  isSuperAdmin,
  onChange,
}: {
  group: SettingGroup;
  settings: AdminSetting[];
  draft: Draft;
  errors: Record<string, string>;
  isSuperAdmin: boolean;
  onChange: (key: string, value: unknown) => void;
}) {
  return (
    <Card>
      <div className="border-b border-line px-5 py-3">
        <h2 className="text-sm font-semibold text-ink">{SETTING_GROUP_LABEL[group]}</h2>
      </div>

      <div className="divide-y divide-line">
        {settings.map((setting) => (
          <SettingRow
            key={setting.key}
            setting={setting}
            draft={draft}
            error={errors[setting.key]}
            isSuperAdmin={isSuperAdmin}
            onChange={onChange}
          />
        ))}
      </div>
    </Card>
  );
}

function SettingRow({
  setting,
  draft,
  error,
  isSuperAdmin,
  onChange,
}: {
  setting: AdminSetting;
  draft: Draft;
  error?: string;
  isSuperAdmin: boolean;
  onChange: (key: string, value: unknown) => void;
}) {
  const value = setting.key in draft ? draft[setting.key] : setting.value;
  const readOnly = setting.isLocked && !isSuperAdmin;
  const dirty = setting.key in draft && !isSame(setting.value, value);

  return (
    <div className="grid gap-3 px-5 py-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-medium text-ink">{setting.label}</p>
          {setting.isLocked && (
            <Badge tone="warning">
              <Lock className="size-3" aria-hidden="true" />
              Locked
            </Badge>
          )}
          {setting.isPublic && (
            <Badge tone="neutral">
              <Globe className="size-3" aria-hidden="true" />
              Storefront
            </Badge>
          )}
          {dirty && <Badge tone="brand">Edited</Badge>}
        </div>

        <p className="mt-0.5 font-mono text-xs text-ink-subtle">{setting.key}</p>

        {setting.description && (
          <p className="mt-1 text-xs text-ink-muted">{setting.description}</p>
        )}

        <p className="mt-1 text-xs text-ink-subtle">
          Last changed {formatDateTime(setting.updatedAt)}
        </p>
      </div>

      <div className="min-w-0">
        <SettingControl
          setting={setting}
          value={value}
          error={error}
          readOnly={readOnly}
          onChange={(next) => onChange(setting.key, next)}
        />

        {readOnly && (
          <p className="mt-1 text-xs text-ink-subtle">
            Only a super admin can change this — it rewrites how existing records read.
          </p>
        )}
      </div>
    </div>
  );
}

function SettingControl({
  setting,
  value,
  error,
  readOnly,
  onChange,
}: {
  setting: AdminSetting;
  value: unknown;
  error?: string;
  readOnly: boolean;
  onChange: (value: unknown) => void;
}) {
  const id = `setting-${setting.key}`;

  if (setting.type === 'BOOLEAN') {
    return (
      <Field label="Value" htmlFor={id} error={error}>
        <div className="pt-1.5">
          <Checkbox
            id={id}
            label={value === true ? 'On' : 'Off'}
            checked={value === true}
            disabled={readOnly}
            onChange={(event) => onChange(event.target.checked)}
          />
        </div>
      </Field>
    );
  }

  if (setting.type === 'NUMBER') {
    return (
      <Field label="Value" htmlFor={id} error={error}>
        <Input
          id={id}
          type="number"
          value={value === null || value === undefined ? '' : String(value)}
          invalid={Boolean(error)}
          disabled={readOnly}
          // Kept as text: `valueAsNumber` turns a cleared field into NaN, and the
          // server is the one that decides whether a value is a number at all.
          onChange={(event) => onChange(event.target.value)}
        />
      </Field>
    );
  }

  if (setting.type === 'JSON') {
    // Every JSON setting in the table is a list of strings, so the control is a
    // line-per-entry editor rather than a raw JSON box. Anything else falls back
    // to formatted JSON text that the server parses.
    const isList = Array.isArray(value) && value.every((entry) => typeof entry === 'string');

    if (isList) {
      return (
        <Field
          label="Value"
          htmlFor={id}
          error={error}
          hint="One entry per line."
        >
          <Textarea
            id={id}
            rows={Math.max(3, (value as string[]).length + 1)}
            value={(value as string[]).join('\n')}
            invalid={Boolean(error)}
            disabled={readOnly}
            onChange={(event) =>
              onChange(
                event.target.value
                  .split('\n')
                  .map((line) => line.trim())
                  .filter(Boolean),
              )
            }
          />
        </Field>
      );
    }

    return (
      <Field label="Value" htmlFor={id} error={error} hint="JSON.">
        <Textarea
          id={id}
          rows={4}
          value={typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
          invalid={Boolean(error)}
          disabled={readOnly}
          onChange={(event) => onChange(event.target.value)}
        />
      </Field>
    );
  }

  const text = typeof value === 'string' ? value : String(value ?? '');

  return (
    <Field label="Value" htmlFor={id} error={error}>
      {text.length > TEXTAREA_THRESHOLD ? (
        <Textarea
          id={id}
          rows={5}
          value={text}
          invalid={Boolean(error)}
          disabled={readOnly}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <Input
          id={id}
          value={text}
          invalid={Boolean(error)}
          disabled={readOnly}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </Field>
  );
}

/**
 * Whether a draft still matches what is stored.
 *
 * Compared as JSON so a document list edited back to its original order counts
 * as unchanged, and loosely for numbers because the input hands back `"18"`
 * where the server sent `18` — without that, opening a number field and
 * touching nothing would mark it dirty forever.
 */
function isSame(stored: unknown, drafted: unknown): boolean {
  if (typeof stored === 'number') {
    return String(stored) === String(drafted).trim();
  }

  return JSON.stringify(stored) === JSON.stringify(drafted);
}
